import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

/**
 * Component for handling delegation and voting power management
 */
const DelegationManager = ({
  governanceContract,
  account,
  setError,
  setSuccess,
  setLoading,
  loading,
  loadUserGovernanceData
}) => {
  // State for delegation UI
  const [delegateAddress, setDelegateAddress] = useState('');
  const [showDelegationPanel, setShowDelegationPanel] = useState(false);
  
  // State for user delegation info
  const [delegatedTo, setDelegatedTo] = useState(null);
  const [delegators, setDelegators] = useState([]);
  const [loadingDelegators, setLoadingDelegators] = useState(false);
  
  // Load delegation data
  useEffect(() => {
    if (governanceContract && account) {
      checkDelegationStatus();
    }
  }, [governanceContract, account]);
  
  // Check if user is delegating or has delegators
  const checkDelegationStatus = async () => {
    try {
      if (!governanceContract) return;
      
      // Check if user is delegating to anyone
      try {
        const delegatee = await governanceContract.delegations(account);
        if (delegatee && delegatee !== ethers.constants.AddressZero) {
          setDelegatedTo(delegatee);
        } else {
          setDelegatedTo(null);
        }
      } catch (error) {
        console.error("Error checking delegation status:", error);
        setDelegatedTo(null);
      }
      
      // Check if user has any delegators
      setLoadingDelegators(true);
      try {
        const userDelegators = await governanceContract.getDelegators(account);
        if (userDelegators && userDelegators.length > 0) {
          setDelegators(userDelegators);
        } else {
          setDelegators([]);
        }
      } catch (error) {
        console.error("Error fetching delegators:", error);
        setDelegators([]);
      } finally {
        setLoadingDelegators(false);
      }
    } catch (error) {
      console.error("Error in checkDelegationStatus:", error);
    }
  };
  
  // Handle delegation to another address
  const handleDelegate = async () => {
    if (!delegateAddress || !ethers.utils.isAddress(delegateAddress)) {
      setError('Please enter a valid Ethereum address');
      return;
    }
    
    if (delegateAddress.toLowerCase() === account.toLowerCase()) {
      setError('You cannot delegate to yourself');
      return;
    }
    
    setLoading(true);
    try {
      // Call the delegation function
      const tx = await governanceContract.delegateTokens(delegateAddress);
      await tx.wait();
      
      setSuccess(`Successfully delegated your voting power to ${delegateAddress}`);
      setDelegateAddress('');
      setShowDelegationPanel(false);
      
      // Refresh delegation status
      await checkDelegationStatus();
      // Refresh user's voting power
      if (typeof loadUserGovernanceData === 'function') {
        await loadUserGovernanceData();
      }
    } catch (error) {
      console.error('Error delegating tokens:', error);
      setError(error.message || 'Failed to delegate tokens');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle removing delegation
  const handleRemoveDelegation = async () => {
    setLoading(true);
    try {
      const tx = await governanceContract.removeDelegation();
      await tx.wait();
      
      setSuccess('Successfully removed your delegation');
      setDelegatedTo(null);
      
      // Refresh user's voting power
      if (typeof loadUserGovernanceData === 'function') {
        await loadUserGovernanceData();
      }
    } catch (error) {
      console.error('Error removing delegation:', error);
      setError(error.message || 'Failed to remove delegation');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-indigo-800">Delegation Management</h3>
        {!delegatedTo && !showDelegationPanel && (
          <button 
            className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
            onClick={() => setShowDelegationPanel(true)}
          >
            Delegate Voting Power
          </button>
        )}
      </div>
      
      {/* Current delegation status */}
      {delegatedTo && (
        <div className="mb-4 p-3 bg-white rounded-md shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-medium">Currently delegating to:</div>
              <div className="text-md text-indigo-700">{delegatedTo}</div>
              <div className="text-xs text-gray-500 mt-1">
                Your voting power is being used by this address
              </div>
            </div>
            <button 
              className="px-3 py-1 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
              onClick={handleRemoveDelegation}
              disabled={loading}
            >
              {loading ? "Processing..." : "Remove Delegation"}
            </button>
          </div>
        </div>
      )}
      
      {/* Delegation form */}
      {showDelegationPanel && !delegatedTo && (
        <div className="mb-4 p-3 bg-white rounded-md shadow-sm">
          <h4 className="text-sm font-medium mb-2">Delegate your voting power to another address</h4>
          <div className="flex space-x-2">
            <input
              type="text"
              className="flex-1 p-2 border rounded-md"
              placeholder="Delegatee address (0x...)"
              value={delegateAddress}
              onChange={(e) => setDelegateAddress(e.target.value)}
            />
            <button
              className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 disabled:bg-gray-400"
              onClick={handleDelegate}
              disabled={loading || !delegateAddress}
            >
              {loading ? "Processing..." : "Delegate"}
            </button>
            <button
              className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400"
              onClick={() => {
                setShowDelegationPanel(false);
                setDelegateAddress('');
              }}
            >
              Cancel
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            This will allow the address to vote with your tokens' voting power
          </div>
        </div>
      )}
      
      {/* Delegators to this account */}
      {delegators.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-medium text-indigo-700 mb-2">Addresses delegating to you:</h4>
          <div className="max-h-32 overflow-y-auto">
            {delegators.map((delegator, index) => (
              <div key={index} className="text-sm p-2 bg-white rounded-md mb-1 shadow-sm">
                {delegator}
              </div>
            ))}
          </div>
          <div className="text-xs text-indigo-600 mt-1">
            You are voting with the combined power of your tokens and delegated tokens
          </div>
        </div>
      )}
      
      {/* Explainer */}
      <div className="text-xs text-indigo-600 mt-3">
        <p className="font-medium">About Delegation:</p>
        <p>You can delegate your voting power to another address while keeping your tokens. This lets trusted addresses vote on your behalf without transferring token ownership.</p>
      </div>
    </div>
  );
};

export default DelegationManager;